/**
 * ScriptQueueManager
 *
 * Maintains a local FIFO queue of pending provisioning script payloads.
 * Scripts arrive pre-ordered by creation time from the API.
 * Only one script executes at a time; the rest wait in the queue.
 *
 * Pattern: Single-responsibility manager (no external dependencies)
 */

export interface QueuedScript {
  deploymentId: string;
  executionId: string;
  scriptId: string;
  scriptName: string;
  scriptType: string;
  completeScript: string;
  signature: string;
  pauseMatchmaking: boolean;
  deploymentMode: string;
  timeoutSeconds?: number;
  /** 0-based step index for multi-step scripts. Echoed back on STARTED/RESULT. */
  stepIndex?: number;
  /** Step display name (e.g. "Install drivers"). */
  stepName?: string;
  /** Total step count for the parent script. */
  stepCount?: number;
}

export class ScriptQueueManager {
  private readonly queue: QueuedScript[] = [];
  private executing: QueuedScript | null = null;

  enqueue(script: QueuedScript): void {
    console.log(
      '[ScriptQueueManager] Enqueued script: deployment=%s execution=%s name="%s"',
      script.deploymentId,
      script.executionId,
      script.scriptName,
    );
    this.queue.push(script);
  }

  /**
   * Remove all queued (not executing) scripts matching the given deploymentId.
   * Returns the number of scripts removed.
   */
  revoke(deploymentId: string): number {
    const before = this.queue.length;
    for (let i = this.queue.length - 1; i >= 0; i--) {
      if (this.queue[i].deploymentId === deploymentId) {
        this.queue.splice(i, 1);
      }
    }
    const removed = before - this.queue.length;
    if (removed > 0) {
      console.log('[ScriptQueueManager] Revoked %d queued scripts for deployment=%s', removed, deploymentId);
    }
    return removed;
  }

  isCurrentlyExecuting(): boolean {
    return this.executing !== null;
  }

  getCurrentExecution(): QueuedScript | null {
    return this.executing;
  }

  isExecutingDeployment(deploymentId: string): boolean {
    return this.executing?.deploymentId === deploymentId;
  }

  /**
   * Dequeue the next script and mark it as the active execution.
   * Returns null if the queue is empty or a script is already executing.
   */
  dequeueNext(): QueuedScript | null {
    if (this.executing) {
      return null;
    }
    const next = this.queue.shift() ?? null;
    if (next) {
      this.executing = next;
      console.log(
        '[ScriptQueueManager] Dequeued script: deployment=%s execution=%s (%d remaining)',
        next.deploymentId,
        next.executionId,
        this.queue.length,
      );
    }
    return next;
  }

  /**
   * Mark the current execution as complete and clear the active slot.
   */
  markComplete(): void {
    if (this.executing) {
      console.log(
        '[ScriptQueueManager] Execution complete: deployment=%s execution=%s',
        this.executing.deploymentId,
        this.executing.executionId,
      );
      this.executing = null;
    }
  }

  getPendingCount(): number {
    return this.queue.length;
  }
}
